import React from "react";

import { Badge, Button, Heading, Icon, ListRow, Text } from "@/components/ui";
import type { EditorProgrammeMetadata, ProgrammeGovernanceSummary } from "@/platform";

export interface ProgrammeLineageProps {
  governance: ProgrammeGovernanceSummary;
  programme: EditorProgrammeMetadata;
  onRelationshipOpen?: (programmeId: string) => void;
}

export function ProgrammeLineage({
  governance,
  programme,
  onRelationshipOpen = undefined,
}: ProgrammeLineageProps): React.ReactElement | null {
  if (governance.programmeRelationships.length === 0) {
    return null;
  }

  return (
    <section className="mt-4" data-testid="programme-relationship-history">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Programme lineage
          </Heading>
          <Text tone="muted" className="mt-1">
            Replacement links show how separately approved programmes are related.
          </Text>
        </div>
        <Badge variant="outline">{governance.programmeRelationships.length} linked</Badge>
      </div>
      <ul className="mt-3 grid gap-2">
        {governance.programmeRelationships.map((relationship) => {
          const isReplacement = relationship.fromProgrammeId === programme.id;
          const relatedProgrammeId = isReplacement
            ? relationship.toProgrammeId
            : relationship.fromProgrammeId;
          return (
            <li key={relationship.id}>
              <ListRow
                data-testid={
                  isReplacement
                    ? "programme-relationship-replaces"
                    : "programme-relationship-replaced-by"
                }
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={isReplacement ? "primary" : "warning"}>
                        {isReplacement ? "Replaces" : "Replaced by"}
                      </Badge>
                      {relationship.effectiveFrom ? (
                        <Text as="span" size="sm" tone="muted">
                          Effective {relationship.effectiveFrom}
                        </Text>
                      ) : null}
                    </div>
                    <Text tone="muted" className="mt-2">
                      {relationship.rationale}
                    </Text>
                  </div>
                  {onRelationshipOpen ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onRelationshipOpen(relatedProgrammeId)}
                      data-testid={`open-related-programme-${relatedProgrammeId}`}
                    >
                      <Icon name="arrow-square-out" aria-hidden />
                      {isReplacement ? "Open earlier programme" : "Open replacement"}
                    </Button>
                  ) : null}
                </div>
              </ListRow>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
